import { HttpErrorResponse, HttpEvent, HttpEventType, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { LoggerService, tryNormalizeHttpError, WindowRef } from '@spartacus/core';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';

import { DpBaseSiteService } from '@app/core/services/base-site.service';
import { DpBaseStoreService } from '@app/core/services/base-store.service';
import { DpCMSPages } from '@app/core/types/common/dp-cms-pages.config';
import { ERROR_TYPE } from '@app/core/types/error-type.enum';
import { isDpHttpError } from '@app/core/types/guards/is-dp-http-error-response.type-guard';
import { DpHttpErrorResponse } from '@app/core/types/http-error-response.interface';

@Injectable()
export class DpHttpInterceptor implements HttpInterceptor {
  private readonly allowedPagesToVisitWhenShopClosed = ['productDetails', 'productList', 'WishListPage'];

  readonly #logger = inject(LoggerService);
  readonly #winRef = inject(WindowRef);

  constructor(
    private readonly router: Router,
    private readonly dpBaseSiteService: DpBaseSiteService,
    private readonly baseStoreService: DpBaseStoreService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      tap((event) => {
        if (event.type === HttpEventType.Response && event.headers.has('is-shop-open')) {
          const isOpenHeader: string = event.headers.get('is-shop-open') ?? '';
          const isOpen: boolean = isOpenHeader === 'true';

          this.baseStoreService.setShopOpenValue(isOpen);

          if (!isOpen && !this.allowedPagesToVisitWhenShopClosed.includes(event.body.uid)) {
            this.router.navigateByUrl(DpCMSPages.ShopClosed.route);
          }
        }
      }),
      catchError((errorResponse: HttpErrorResponse | DpHttpErrorResponse) => {
        if(errorResponse?.error?.error === 'invalid_grant') {
          return throwError(() => errorResponse);
        } else if (!isDpHttpError(errorResponse)) {
          return throwError(() => tryNormalizeHttpError(errorResponse, this.#logger));
        }

        this.handleHttpError(errorResponse as DpHttpErrorResponse);

        return throwError(() => errorResponse);
      })
    );
  }

  private handleHttpError(errorResponse: DpHttpErrorResponse): void {
    const {
      errors: [error]
    } = errorResponse.error;
    const [urlMarket] = this.router.url.split('/');
    switch (error.type) {
      case ERROR_TYPE.UNSUPPORTED_LANGUAGE:
        this.handleUnsupportedLanguage(urlMarket);
        break;
    }
  }

  private handleUnsupportedLanguage(market: string): void {
    this.detectMarketDefaultLanguage(market)
      .pipe(take(1))
      .subscribe((defaultMarketLanguage) => {
        this.#winRef?.location?.replace?.(`${market}/${defaultMarketLanguage}`);
      });
  }

  private detectMarketDefaultLanguage(id: string): Observable<string | unknown> {
    return this.dpBaseSiteService.get(id).pipe(
      filter((market) => !!market),
      map((market) => market?.defaultLanguage?.isocode)
    );
  }
}
